import React, { useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Button,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText, DialogActions,
  Stack,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import {
  usePatchInviteByIdRevokeMutation,
  InviteWithRelations,
  Invite,
  useGetMyPropertyInvitesQuery,
  useGetPropertiesOwnerByOwnerIdQuery,
  Property,
} from "../../redux/residentPortalApi";
import { format, isAfter, parseISO } from 'date-fns';
import {usePatchInviteByIdResendMutation} from '../../redux/inviteResendApi';
import config from '../../config';
import { useAppSelector } from '../../redux/hooks';
import { developmentInfo, formatPropertyNumber } from '../../utils/propertyUtils';

type StatusFilter = 'all' | 'active' | 'expired' | 'revoked' | 'scanned';
type SortOrder = 'newest' | 'oldest';

interface EnrichedInvite {
  invite: Invite;
  developmentLabel: string;
  date: Date | null;
  period: string;
  isExpired: boolean;
  isScanned: boolean;
  isRevoked: boolean;
  isActive: boolean;
}

const PAGE_SIZE = 20;

interface PreviousInvitesProps {
  onReinvite?: (invite: Invite) => void;
}

const PreviousInvites: React.FC<PreviousInvitesProps> = ({ onReinvite }) => {
  const user = useAppSelector(state => state.auth.user);
  const { data: invites, isLoading, error, refetch } = useGetMyPropertyInvitesQuery({});
  const { data: properties } = useGetPropertiesOwnerByOwnerIdQuery(
    { ownerId: user?.id ?? '' },
    { skip: !user }
  );
  const [revokeInvite, { isLoading: isRevoking }] = usePatchInviteByIdRevokeMutation();
  const [resendInvite, { isLoading: isResending }] = usePatchInviteByIdResendMutation();
  const [selectedInvite, setSelectedInvite] = useState<InviteWithRelations | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [developmentFilter, setDevelopmentFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const propertiesById = useMemo(() => {
    const map = new Map<string, Property>();
    (properties ?? []).forEach(property => {
      if (property.id) map.set(property.id, property);
    });
    return map;
  }, [properties]);

  const getDevelopmentLabel = (invite: Invite): string => {
    const property = invite.propertyId ? propertiesById.get(invite.propertyId) : undefined;
    if (!property) return 'Unknown Development';
    const info = developmentInfo(property.development, property.phase, property.type);
    const unit = formatPropertyNumber(property);
    return info.name ? (unit ? `${info.name} - ${unit}` : info.name) : 'Unknown Development';
  };

  const enrichedInvites: EnrichedInvite[] = useMemo(() => {
    return (invites ?? []).map((invite: Invite) => {
      const rawDate = invite.isMultiDay ? invite.startDate : invite.visitDateTime;
      const date = rawDate ? parseISO(rawDate) : null;
      const isExpired = invite.endDate ? isAfter(new Date(), parseISO(invite.endDate)) : false;
      const isRevoked = !!invite.revoked;
      return {
        invite,
        developmentLabel: getDevelopmentLabel(invite),
        date,
        period: date ? format(date, 'MMMM yyyy') : 'Undated',
        isExpired,
        isScanned: !!invite.scanned,
        isRevoked,
        isActive: !isExpired && !isRevoked,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    });
  }, [invites, propertiesById]);

  const developmentOptions = useMemo(() => {
    const labels = new Set<string>();
    enrichedInvites.forEach(item => labels.add(item.developmentLabel));
    return Array.from(labels).sort((a, b) => a.localeCompare(b));
  }, [enrichedInvites]);

  const filteredSorted = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const result = enrichedInvites.filter(item => {
      if (term && !item.invite.visitorName?.toLowerCase().includes(term)) return false;
      if (developmentFilter !== 'all' && item.developmentLabel !== developmentFilter) return false;
      if (statusFilter === 'active' && !item.isActive) return false;
      if (statusFilter === 'expired' && !item.isExpired) return false;
      if (statusFilter === 'revoked' && !item.isRevoked) return false;
      if (statusFilter === 'scanned' && !item.isScanned) return false;
      return true;
    });

    result.sort((a, b) => {
      const aTime = a.date ? a.date.getTime() : 0;
      const bTime = b.date ? b.date.getTime() : 0;
      return sortOrder === 'newest' ? bTime - aTime : aTime - bTime;
    });

    return result;
  }, [enrichedInvites, searchTerm, developmentFilter, statusFilter, sortOrder]);

  const visibleItems = filteredSorted.slice(0, visibleCount);

  const groupedByPeriod = useMemo(() => {
    const groups: { period: string; items: EnrichedInvite[] }[] = [];
    visibleItems.forEach(item => {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.period === item.period) {
        lastGroup.items.push(item);
      } else {
        groups.push({ period: item.period, items: [item] });
      }
    });
    return groups;
  }, [visibleItems]);

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDevelopmentFilter('all');
    setSortOrder('newest');
    setVisibleCount(PAGE_SIZE);
  };

  const handleRevokeClick = (invite: InviteWithRelations) => {
    setActionMessage(null);
    setActionError(null);
    setRevokeError(null);
    setSelectedInvite(invite);
    setOpenDialog(true);
  };

  const handleResendClick = async (invite: InviteWithRelations) => {
    if (!invite.id) {
      return;
    }

    try {
      setActionMessage(null);
      setActionError(null);
      const response = await resendInvite({id: invite.id}).unwrap();
      setActionMessage(response.message || `Invite resent to ${invite.visitorName}.`);
    } catch (resendError) {
      console.error('Failed to resend invite:', resendError);
      setActionError('Failed to resend invite. Please try again.');
    }
  };

  const handleConfirmRevoke = async () => {
    if (selectedInvite && selectedInvite.id) {
      try {
        await revokeInvite({ id: selectedInvite.id }).unwrap();
        setOpenDialog(false);
        await refetch();
      } catch (error) {
        console.error('Failed to revoke invite:', error);
        setRevokeError('Failed to revoke invite. Please try again.');
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedInvite(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error loading invites. Please try again later.
      </Alert>
    );
  }

  if (!invites || invites.length === 0) {
    return (
      <Card raised sx={{ borderRadius: '20px', margin: 2, boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>Previous Invites</Typography>
          <Typography variant="body1">No previous invites found.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card raised sx={{ borderRadius: '20px', margin: 2, boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)' }}>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} mb={2}>
          <Typography variant="h5" gutterBottom sx={{ mb: { sm: 0 } }}>Previous Invites</Typography>
          <Chip
            label={`${filteredSorted.length} of ${invites.length} invite${invites.length === 1 ? '' : 's'}`}
            size="small"
            variant="outlined"
          />
        </Stack>

        {actionMessage && (
          <Alert severity="success" sx={{mb: 2}}>
            {actionMessage}
          </Alert>
        )}
        {actionError && (
          <Alert severity="error" sx={{mb: 2}}>
            {actionError}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            p: 2,
            mb: 2,
            borderRadius: 3,
            backgroundColor: '#f8f9fb',
            border: '1px solid #eaeef2',
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            placeholder="Search by visitor name"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setVisibleCount(PAGE_SIZE); }}
            sx={{ minWidth: 220, flex: '1 1 220px', backgroundColor: '#fff', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150, backgroundColor: '#fff', borderRadius: 1 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setVisibleCount(PAGE_SIZE); }}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="scanned">Scanned</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="revoked">Revoked</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180, backgroundColor: '#fff', borderRadius: 1 }}>
            <InputLabel id="development-filter-label">Development</InputLabel>
            <Select
              labelId="development-filter-label"
              label="Development"
              value={developmentFilter}
              onChange={(e) => { setDevelopmentFilter(e.target.value); setVisibleCount(PAGE_SIZE); }}
            >
              <MenuItem value="all">All developments</MenuItem>
              {developmentOptions.map(label => (
                <MenuItem key={label} value={label}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150, backgroundColor: '#fff', borderRadius: 1 }}>
            <InputLabel id="sort-order-label">Sort by</InputLabel>
            <Select
              labelId="sort-order-label"
              label="Sort by"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as SortOrder)}
            >
              <MenuItem value="newest">Newest first</MenuItem>
              <MenuItem value="oldest">Oldest first</MenuItem>
            </Select>
          </FormControl>

          <Button
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={handleResetFilters}
            sx={{ ml: { sm: 'auto' } }}
          >
            Reset
          </Button>
        </Box>

        {filteredSorted.length === 0 ? (
          <Typography variant="body1" sx={{ py: 4, textAlign: 'center' }} color="text.secondary">
            No invites match your search or filters.
          </Typography>
        ) : (
          <List sx={{ p: 0 }}>
            {groupedByPeriod.map(group => (
              <React.Fragment key={group.period}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 1,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    backgroundColor: '#fff',
                  }}
                >
                  <EventNoteOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {group.period}
                  </Typography>
                  <Chip label={group.items.length} size="small" />
                </Box>
                {group.items.map(({ invite, developmentLabel, isExpired, isScanned, isRevoked, isActive }) => (
                  <React.Fragment key={invite.id}>
                    <ListItem alignItems="flex-start" secondaryAction={
                      <Stack direction="row" spacing={1}>
                        {onReinvite && (
                          <Button
                            variant="outlined"
                            onClick={() => onReinvite(invite)}
                          >
                            Reinvite
                          </Button>
                        )}
                        {!isRevoked && (
                          <Button
                            variant="outlined"
                            onClick={() => handleResendClick(invite)}
                            disabled={isResending}
                          >
                            {isResending ? 'Resending...' : 'Resend'}
                          </Button>
                        )}
                        {!isRevoked && invite.id && (
                          <Button
                            variant="outlined"
                            component="a"
                            href={`${config.CDN_URL}/invites/${invite.id}.png`}
                            target="_blank"
                            rel="noreferrer"
                            download
                          >
                            Download Pass
                          </Button>
                        )}
                        {!isExpired && !isRevoked && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleRevokeClick(invite)}
                          >
                            Revoke
                          </Button>
                        )}
                      </Stack>
                    }>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#e8edf5', color: '#345678' }}>
                          <PersonOutlineIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {invite.visitorName}
                            </Typography>
                            <Typography component="span" variant="body2" color="text.secondary">
                              {developmentLabel}
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <>
                            <Typography component="span" variant="body2" color="textPrimary">
                              {invite.isMultiDay
                                ? `From ${invite.startDate ? format(parseISO(invite.startDate), 'dd MMM yyyy') : 'N/A'} to ${invite.endDate ? format(parseISO(invite.endDate), 'dd MMM yyyy') : 'N/A'}`
                                : `On ${invite.visitDateTime ? format(parseISO(invite.visitDateTime), 'dd MMM yyyy, HH:mm') : 'N/A'}`}
                            </Typography>
                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                              {isActive && <Chip label="Active" size="small" color="primary" variant="outlined" />}
                              {isExpired && <Chip label="Expired" size="small" color="default" variant="outlined" />}
                              {isScanned && <Chip label="Scanned" size="small" color="success" variant="outlined" />}
                              {isRevoked && <Chip label="Revoked" size="small" color="error" variant="outlined" />}
                            </Stack>
                          </>
                        }
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </List>
        )}

        {visibleCount < filteredSorted.length && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="outlined" onClick={() => setVisibleCount(count => count + PAGE_SIZE)}>
              Load more ({filteredSorted.length - visibleCount} remaining)
            </Button>
          </Box>
        )}
      </CardContent>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
      >
        <DialogTitle>Revoke Invite</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke the invite for {selectedInvite?.visitorName}? 
            This action cannot be undone.
          </DialogContentText>
          {revokeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {revokeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isRevoking}>Cancel</Button>
          <Button 
            onClick={handleConfirmRevoke} 
            color="error" 
            autoFocus
            disabled={isRevoking}
            startIcon={isRevoking ? <CircularProgress size={20} /> : undefined}
          >
            {isRevoking ? 'Revoking...' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PreviousInvites;